import styles from "./QRBol.module.scss";
import { useEffect, useState, useCallback } from "react";
import { Button } from "UI/Button/Button";
import Layout from "layout/Layout";
import Hash from "components/Hash/Hash";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Modal } from "UI/Modal/Modal";
import SbpPaySVG from "svg/payMethods/SbpPaySVG";
import { QRCodeSVG } from "qrcode.react";
import TimerComponent from "components/Timer/Timer";
import useUrlData from "store/DataStore";
import { getDeviceInfo, copyToClipboard } from "common/commonFunctions";
import useDeviceStore from "store/DeviceStore";
import useErrorStore from "store/errorStore";
import { generatePaymentLink, getOrderData } from "services/api";
import { Loader } from "UI/Loader/Loader";
import { trackPaymentClick } from "services/analytics";
import useTimerData from "store/TimerStore";

const POLLING_INTERVAL = 10000; // 10 seconds

const QRBol = () => {
  const { t } = useTranslation();
  const { orderId } = useParams<{ orderId: string }>();
  const { data: allData, loading, error, fetchOrderData, mapServerData, setMockData } = useUrlData();
  const [isPay, setIsPay] = useState(false);
  const navigate = useNavigate();
  const [onCancel, setOnCancel] = useState(false);
  const { setDevice } = useDeviceStore();
  const { networkErr } = useErrorStore((state) => state);
  const [copiedAmount, setCopiedAmount] = useState(false);
  const [copiedCBU, setCopiedCBU] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const { calculateRemaining } = useTimerData();

  // Load mock data if orderId is "mock"
  useEffect(() => {
    if (orderId === 'mock') {
      setMockData({
        payment_id: "88770f35-e539-5667-b1gg-cd8824e4cd5f",
        amount: 10,
        currency: "BOB",
        address: "62382637",
        qrData: "Gm61LeP2fvkAmHRIqvf29iXPEYHBNhGBJNW4cBVpeH8v5QSLgh7WJsA9aGN6le1U8W3ae227edGaCs2Z4EEKbH74cFZOwv7//UIze6MWwyU8Puk7piJTIXyB3VA8YV2fjp6Z8KyR9Zo+1DbIM8Z47Bg3AhAxvgZy3BHlOEmBCu5CzMGwkpLacIpCxg36cyklH88RpPZuOsYxPd9aeUQf839GhhypE4Wavl0pYNJQh34C2RIAD9Y3TDnoL8IyJB9tDPXdg5OocsjgQRDZI3+iCd89AVW6cK/n5yN0TE0pqnRjud6wACLlt6ZZUE+oo0zaMjt60L4bRNP9ZqIrGWUgSg==|0FABBA22E4072538599B68591360A676",
        bank_name: "Yape",
        status_name: "pending",
        expires_in: 600,
      });
    }
  }, [orderId, setMockData]);

  // Check status and redirect if needed
  const checkStatusAndRedirect = useCallback((status: string) => {
    const normalizedStatus = status.toLowerCase();
    
    if (normalizedStatus === 'expired') {
      navigate(`/canceled/${orderId}?type=expired`, { replace: true });
      return true;
    }
    
    if (normalizedStatus === 'cancelled' || normalizedStatus === 'canceled') {
      navigate(`/canceled/${orderId}?type=canceled`, { replace: true });
      return true;
    }
    
    if (normalizedStatus === 'completed' || normalizedStatus === 'finished' || normalizedStatus === 'success') {
      navigate(`/success/${orderId}`, { replace: true });
      return true;
    }
    
    return false;
  }, [navigate, orderId]);

  // Fetch order data from server only if not already loaded and not mock
  useEffect(() => {
    if (orderId && orderId !== 'mock' && !allData.payment_id) {
      fetchOrderData(orderId).catch(console.error);
    }
  }, [orderId, fetchOrderData, allData.payment_id]);

  // Check status on initial load
  useEffect(() => {
    if (!loading && allData.status_name) {
      checkStatusAndRedirect(allData.status_name);
    }
  }, [loading, allData.status_name, checkStatusAndRedirect]);

  // Polling for status updates every 10 seconds (skip for mock)
  useEffect(() => {
    if (!orderId || orderId === 'mock') return;

    const pollStatus = async () => {
      try {
        const data = await getOrderData(orderId);
        mapServerData(data);
        
        // Check if status changed and redirect
        if (data.state) {
          checkStatusAndRedirect(data.state);
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    };

    const intervalId = setInterval(pollStatus, POLLING_INTERVAL);

    return () => clearInterval(intervalId);
  }, [orderId, mapServerData, checkStatusAndRedirect]);

  useEffect(() => {
    getDeviceInfo(setDevice);
  }, [allData, networkErr, setDevice]);

  // Handle copy
  const handleCopy = async (text: string, setCopied: (value: boolean) => void) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    }
  };

  // Format amount for display - Bolivia specific
  const formatAmount = (amount: number, currency?: string) => {
    if (currency === 'BOB') {
      return `${amount.toLocaleString('es-BO')} BOB`;
    }
    return `${amount.toLocaleString()} ${currency || ''}`;
  };

  // Handle Bank payment for Bolivia
  const handleBankPayment = async () => {
    if (!allData.payment_id || !allData.address) return;
    
    // Copy CBU to clipboard first
    const success = await copyToClipboard(allData.address);
    if (success) {
      setCopiedCBU(true);
      setTimeout(() => setCopiedCBU(false), 3000);
    }
    
    setPaymentLoading(true);
    
    // Wait 1 second before opening Bank app
    setTimeout(async () => {
      try {
        const { paymentUrl } = await generatePaymentLink(allData.payment_id, allData.address || '');
        
        // Open bank app or fallback
        window.location.href = paymentUrl;
      } catch (error) {
        console.error('Payment error:', error);
        setPaymentLoading(false);
      }
    }, 1000);
  };

  // DEBUG: Test methods for Mercado Pago deep links
  const handleTest1 = async () => {
    await copyToClipboard(allData.address || '');
    console.log('Test 1: money-out/transfer/amount with amount');
    window.location.href = `mercadopago://money-out/transfer/amount?amount=${allData.amount}`;
  };

  const handleTest2 = async () => {
    await copyToClipboard(allData.address || '');
    console.log('Test 2: money-out/transfer/');
    window.location.href = 'mercadopago://money-out/transfer/';
  };

  const handleTest3 = async () => {
    await copyToClipboard(allData.address || '');
    console.log('Test 3: money-out/transfers/unified/input');
    window.location.href = 'mercadopago://money-out/transfers/unified/input';
  };

  const handleTest4 = async () => {
    await copyToClipboard(allData.address || '');
    console.log('Test 4: money-out/home');
    window.location.href = 'mercadopago://money-out/home';
  };

  const handleTest5 = async () => {
    await copyToClipboard(allData.address || '');
    console.log('Test 5: my-money');
    window.location.href = 'mercadopago://my-money';
  };

  // QR data - use server qrData or generate from address
  const qrValue = allData.qrData || allData.address || '';

  if (loading) {
    return (
      <Layout headerText="" headerStep="" currentStep={2} hidePayments>
        <div className={styles.loaderWrapper}>
          <Loader />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout headerText={t("Ошибка")} headerStep="" currentStep={2} hidePayments>
        <div className={styles.errorWrapper}>
          <p>{error}</p>
          <Button onClick={() => navigate("/")} variant="yellow">
            {t("На главную")}
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      headerText={t("Скопируйте реквизиты получателя")}
      headerStep={t("2. Оплата.")}
      currentStep={2}
      hidePayments
    >
        <div className={styles.wrapper}>
          <div className={styles.up}>
            <Hash />
            <TimerComponent />
          </div>

          <div className={styles.content}>
            <div className={styles.info}>
              <div className={styles.value}>
                <p>{formatAmount(allData.amount, allData.currency)}</p>
              </div>
              <div className={styles.desc}>
                {t("Отсканируйте QR-код с камеры вашего устройства")}
              </div>
            </div>
            
            <div className={styles.qrContainer}>
              <div className={styles.qrBg}>
                <QRCodeSVG size={100} value={qrValue} />
              </div>
            </div>

            {/* PayBlock with real data */}
            <div className={styles.payBlock} style={{ margin: '20px 0' }}>
              <div style={{
                background: '#FFFFFF',
                borderRadius: '16px',
                overflow: 'hidden'
              }}>
                <div style={{ padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src="/banks/yape.png" alt="" style={{ width: '48px', height: '48px', objectFit: 'contain' }} />
                      </div>
                      <div>
                        <span style={{ fontSize: '12px', color: '#6B6878', display: 'block' }}>
                          {t("Recipient's bank")}
                        </span>
                        <p style={{ fontSize: '16px', color: '#1A1825', margin: '4px 0 0 0', fontWeight: 600 }}>
                          {allData.bank_name || "Yape"}
                        </p>
                      </div>
                    </div>
                    <div>
                      <img src="/banks/afip_1.svg" alt="" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                    </div>
                  </div>
                </div>
                <div style={{ padding: '16px', borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
                  <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <span style={{ fontSize: '12px', color: '#6B6878', display: 'block' }}>
                        {t("Amount to transfer")}
                      </span>
                      <p style={{ fontSize: '16px', color: '#1A1825', margin: '4px 0 0 0', fontWeight: 600 }}>
                        {formatAmount(allData.amount, allData.currency)}
                      </p>
                    </div>
                    <button 
                      onClick={() => handleCopy(String(allData.amount), setCopiedAmount)}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 3.13281H4C2.89543 3.13281 2 4.02824 2 5.13281V12.1328C2 13.2374 2.89543 14.1328 4 14.1328H14C15.1046 14.1328 16 13.2374 16 12.1328V5.13281C16 4.02824 15.1046 3.13281 14 3.13281Z" stroke={copiedAmount ? "#1A1825" : "#BFBCC9"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                        <path d="M19 8C20.1 8 21 8.9 21 10V17C21 18.1 20.1 19 19 19H9C7.9 19 7 18.1 7 17V16.6328" stroke={copiedAmount ? "#1A1825" : "#BFBCC9"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                      </svg>
                    </button>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <span style={{ fontSize: '12px', color: '#6B6878', display: 'block' }}>
                        Cedula
                      </span>
                      <p style={{ fontSize: '16px', color: '#1A1825', margin: '4px 0 0 0', fontWeight: 600 }}>
                        {allData.address || '—'}
                      </p>
                    </div>
                    <button 
                      onClick={() => handleCopy(allData.address || '', setCopiedCBU)}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 3.13281H4C2.89543 3.13281 2 4.02824 2 5.13281V12.1328C2 13.2374 2.89543 14.1328 4 14.1328H14C15.1046 14.1328 16 13.2374 16 12.1328V5.13281C16 4.02824 15.1046 3.13281 14 3.13281Z" stroke={copiedCBU ? "#1A1825" : "#BFBCC9"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                        <path d="M19 8C20.1 8 21 8.9 21 10V17C21 18.1 20.1 19 19 19H9C7.9 19 7 18.1 7 17V16.6328" stroke={copiedCBU ? "#1A1825" : "#BFBCC9"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.buttons}>
              {/* DEBUG: Test buttons for Mercado Pago deep links */}
              <div style={{ marginBottom: '20px', padding: '15px', background: '#fff3cd', borderRadius: '8px' }}>
                <strong style={{ display: 'block', marginBottom: '10px', fontSize: '14px' }}>🔧 TEST: Mercado Pago Deep Links</strong>
                
                <button onClick={handleTest1} style={{
                  width: '100%', padding: '10px', marginBottom: '8px', background: '#e3f2fd', 
                  border: '2px solid #2196f3', borderRadius: '6px', fontSize: '13px', cursor: 'pointer'
                }}>
                  <strong>Test 1:</strong> money-out/transfer/amount?amount={allData.amount}
                </button>

                <button onClick={handleTest2} style={{
                  width: '100%', padding: '10px', marginBottom: '8px', background: '#f3e5f5', 
                  border: '2px solid #9c27b0', borderRadius: '6px', fontSize: '13px', cursor: 'pointer'
                }}>
                  <strong>Test 2:</strong> money-out/transfer/
                </button>

                <button onClick={handleTest3} style={{
                  width: '100%', padding: '10px', marginBottom: '8px', background: '#e8f5e9', 
                  border: '2px solid #4caf50', borderRadius: '6px', fontSize: '13px', cursor: 'pointer'
                }}>
                  <strong>Test 3:</strong> money-out/transfers/unified/input
                </button>

                <button onClick={handleTest4} style={{
                  width: '100%', padding: '10px', marginBottom: '8px', background: '#fff3e0', 
                  border: '2px solid #ff9800', borderRadius: '6px', fontSize: '13px', cursor: 'pointer'
                }}>
                  <strong>Test 4:</strong> money-out/home
                </button>

                <button onClick={handleTest5} style={{
                  width: '100%', padding: '10px', marginBottom: '8px', background: '#fce4ec', 
                  border: '2px solid #e91e63', borderRadius: '6px', fontSize: '13px', cursor: 'pointer'
                }}>
                  <strong>Test 5:</strong> my-money
                </button>
              </div>

              <div className={styles.sbpNavBtn} onClick={handleBankPayment}>
                <div className={styles.bankBtn}>
                  <span>Abrir en Banco</span>
                </div>
              </div>

              <Button onClick={() => {
                if (orderId) trackPaymentClick(orderId);
                setIsPay(true);
              }} variant="yellow">
                {t("Оплачено")}
              </Button>              

              {allData.bank_card_number !== null && 
              <>
                <div className={styles.or}>
                  <div />
                  <span>{t("Или выберите другой способ оплаты")}</span>
                  <div />
                </div>
                <div onClick={() => navigate("/payment")} className={styles.payBtn}>
                <SbpPaySVG height="60" width="372" className={styles.paySvg}  />
                </div>
              </>
              }

              <Button variant="link" onClick={() => setOnCancel(true)}>
                {t("Отменить платеж")}
              </Button>
            </div>
          </div>
        </div>
      

      {onCancel && (
        <Modal
          type="cancel"
          goTo={() => navigate(`/canceled/${orderId}?type=canceled`)}
          onClose={() => setOnCancel(false)}
        />
      )}

      {isPay && navigate(`/searchPay/${orderId}`)}
    </Layout>
  );
};

export default QRBol;

